import React, { useEffect, useRef, useState } from 'react';
import SEO from '../../../components/SEO';
import ModeSelectorDialog from '../components/ModeSelectorDialog';
import TrainingHeader from '../components/TrainingHeader';
import {
  AudienceSection,
  BonusesSection,
  CapabilitiesSection,
  FaqSection,
  FinalCtaSection,
  InvestmentSection,
  LaboratorySection,
  LearningMethodSection,
  ModulesSection,
  ProofSection,
  TrainingHero,
  TransformationSection,
} from '../components/TrainingSections';
import { trackTrainingEvent } from '../trainingAnalytics';
import { getTrainingContent } from '../trainingContent';
import { getOffer } from '../trainingOffers';
import { getAlternatePaths, getCataloguePath, getOfferPath } from '../trainingRoutes';
import { buildOfferStructuredData } from '../trainingSeo';
import '../training.css';

const labels = {
  fr: {
    catalogue: 'Toutes les formations',
    sticky: 'Choisir mon mode',
  },
  en: {
    catalogue: 'All training programs',
    sticky: 'Choose my mode',
  },
};

export default function TrainingOfferPage({ locale, offerKey }) {
  const offer = getOffer(offerKey);
  const content = getTrainingContent(locale, offerKey);
  const path = getOfferPath(locale, offerKey);
  const cataloguePath = getCataloguePath(locale);
  const copy = labels[locale];
  const landingPathRef = useRef();
  const [dialog, setDialog] = useState({ open: false, source: 'hero' });

  if (!offer || !content || !path || !cataloguePath || !copy) {
    throw new TypeError(`Unsupported training offer page: ${locale}/${offerKey}`);
  }

  const alternates = getAlternatePaths(path);
  const structuredData = buildOfferStructuredData(locale, offerKey, content);

  useEffect(() => {
    if (landingPathRef.current !== path) {
      landingPathRef.current = path;
      try {
        trackTrainingEvent('landing_view', { offerKey, locale, path });
      } catch {
        // Page rendering must not depend on an analytics adapter.
      }
    }
  }, [locale, offerKey, path]);

  const openDialog = (source) => setDialog({ open: true, source });
  const closeDialog = () => setDialog((current) => ({ ...current, open: false }));

  return (
    <div className="training-page training-offer">
      <SEO
        {...content.seo}
        path={path}
        alternates={alternates}
        structuredData={structuredData}
      />

      <div className="training-offer__shell" inert={dialog.open}>
        <TrainingHeader locale={locale} offerKey={offerKey} />
        <main className="training-offer__main">
          <a className="training-offer__catalogue-link" href={cataloguePath}>{copy.catalogue}</a>
          <TrainingHero locale={locale} offer={offer} content={content.hero} heroImage={content.seo.image} heroAlt={content.seo.title} onCta={openDialog} />
          <AudienceSection locale={locale} offer={offer} content={content.audience} />
          <TransformationSection locale={locale} offer={offer} content={content.transformation} media={content.media} />
          <CapabilitiesSection locale={locale} offer={offer} content={content.capabilities} />
          <ModulesSection locale={locale} offer={offer} content={content.modules} />
          <LearningMethodSection locale={locale} offer={offer} content={content.method} media={content.media} />
          <LaboratorySection locale={locale} offer={offer} content={content.laboratory} media={content.media} />
          <BonusesSection locale={locale} offer={offer} content={content.bonuses} />
          <ProofSection locale={locale} offer={offer} content={content.proof} media={content.media} />
          <InvestmentSection
            locale={locale}
            offer={offer}
            content={content.investment}
            onCta={openDialog}
          />
          <FaqSection locale={locale} offer={offer} content={content.faq} />
          <FinalCtaSection locale={locale} offer={offer} content={content.finalCta} onCta={openDialog} />
        </main>
        <div className="training-offer__sticky-space" aria-hidden="true" />
        <button
          className="training-offer__sticky-cta"
          type="button"
          data-testid="training-cta-sticky"
          hidden={dialog.open}
          inert={dialog.open}
          onClick={() => openDialog('sticky')}
        >
          {copy.sticky}
        </button>
      </div>

      <ModeSelectorDialog
        open={dialog.open}
        locale={locale}
        offer={offer}
        source={dialog.source}
        onClose={closeDialog}
      />
    </div>
  );
}
